import { component } from 'picoapp';
import noUISlider from 'nouislider';
import { debounce } from 'lodash-es';
import Drawer from 'utils/drawer';
import formatMoney from 'utils/format-money';
import app from 'app';  // eslint-disable-line

export default component((node, ctx) => {
  /**
   * Elements
   */
  const sorting = node.querySelector('.js-sorting');
  const filterDrawerElement = node.querySelector('.js-filterDrawer');
  const filterDrawerBtn = node.querySelector('.js-filterDrawerBtn');
  const filterDrawerCloseBtns = node.querySelectorAll('.js-filterDrawerCloseBtn');
  const filterSections = node.querySelectorAll('.js-filterSection');
  const output = node.querySelector('.js-output');

  /**
   * Data
   */
  let currentUrl = window.location.href;

  /**
   * Functions
   */
  const update = (url) => new Promise((resolve) => {
    currentUrl = url.href;
    window.history.pushState({}, '', currentUrl);

    url.searchParams.set('sections', 'collection-main');

    fetch(url.href).then((response) => response.text()).then((text) => JSON.parse(text)).then((sectionMarkups) => { // eslint-disable-line
      const temp = document.createElement('div');
      temp.innerHTML = sectionMarkups['collection-main'];

      const filterSectionMarkup = temp.querySelector('.js-filterSection').innerHTML;
      const outputMarkup = temp.querySelector('.js-output').innerHTML;

      filterSections.forEach((section) => {
        section.innerHTML = filterSectionMarkup;
      });

      output.innerHTML = outputMarkup;

      ctx.emit('collectionFilters:init');

      resolve();
    });
  });

  const initSorting = () => {
    sorting.addEventListener('change', async () => {
      const { value } = sorting;
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('sort_by', value);

      const url = new URL(window.location.href);
      url.search = searchParams;

      await update(url);
    });
  };

  const initPriceFilter = () => {
    const priceFilters = node.querySelectorAll('.js-priceFilter');
    const priceFilterInputs = node.querySelectorAll('.js-priceFilterInput');
    const priceSliderElements = node.querySelectorAll('.js-priceSlider');

    const min = parseInt(priceFilters[0].dataset.min, 10);
    const max = parseInt(priceFilters[0].dataset.max, 10);

    const currentMin = parseInt(priceFilterInputs[0].dataset.min, 10);
    const currentMax = parseInt(priceFilterInputs[0].dataset.max, 10);

    const priceSliders = [...priceSliderElements].map((element) => noUISlider.create(element, {
      start: [
        currentMin,
        currentMax,
      ],
      range: {
        min,
        max,
      },
      step: 100,
      connect: [false, true, false],
      tooltips: {
        to: (value) => formatMoney(value, theme.moneyFormat),
        from: (value) => formatMoney(value, theme.moneyFormat),
      },
    }));

    priceFilterInputs.forEach((input) => {
      const { minParamName, maxParamName } = input.dataset;

      input.addEventListener('change', debounce(async () => {
        const url = new URL(currentUrl);
        url.searchParams.set(minParamName, input.dataset.min / 100);
        url.searchParams.set(maxParamName, input.dataset.max / 100);

        await update(url);
      }), 300);
    });

    priceSliders.forEach((slider) => {
      slider.on('set', () => {
        // Temporarily store the sliders that have been set
        ctx.hydrate({
          priceSliders: ctx.getState().priceSliders ? [...ctx.getState().priceSliders, slider.target] : [slider.target], // eslint-disable-line
        });

        const values = slider.get().map((value) => parseInt(value, 10));

        priceFilters.forEach((priceFilter, index) => {
          const input = priceFilter.querySelector('input');

          [input.dataset.min, input.dataset.max] = values;

          if (index === 0) {
            input.dispatchEvent(new Event('change'));
          }
        });

        priceSliders.forEach((otherPriceSlider) => {
          // Only set current slider, if it hasn't already been set in the current loop
          // - to avoid infinite loop
          if (!ctx.getState().priceSliders.includes(otherPriceSlider.target)) {
            otherPriceSlider.set(values);
          }
        });

        // Finally, clear the list
        setTimeout(() => {
          ctx.hydrate({ priceSliders: [] });
        }, 100);
      });
    });
  };

  const initFilters = () => {
    filterSections.forEach((filterSection) => {
      const clearAllBtn = filterSection.querySelector('.js-clearAllBtn');
      const clearFilterBtns = filterSection.querySelectorAll('.js-clearFilterBtn');
      const filterItems = filterSection.querySelectorAll('.js-filterItem');

      if (clearAllBtn) {
        clearAllBtn.addEventListener('click', async () => {
          const params = new URLSearchParams(window.location.search);

          const filterParamKeys = Array.from(params.entries()).map((pair) => pair[0]).filter((key) => key.split('.')[0] === 'filter');

          filterParamKeys.forEach((key) => {
            params.delete(key);
          });

          const url = new URL(window.location.href);
          url.search = params.toString();

          await update(url);
        });
      }

      clearFilterBtns.forEach((btn) => {
        btn.addEventListener('click', async () => {
          const { paramName } = btn.dataset;
          const params = new URLSearchParams(window.location.search);
          params.delete(paramName);

          const url = new URL(window.location.href);
          url.search = params.toString();

          await update(url);
        });
      });

      filterItems.forEach((item) => {
        item.addEventListener('click', async (event) => {
          event.preventDefault();

          item.classList.toggle('collection__filter__item--active');

          const paramString = item.dataset.value.split('?')[1] || '';
          const url = new URL(currentUrl);
          url.search = paramString;

          await update(url);
        });
      });
    });
  };

  const initFilterDrawer = () => {
    const filterDrawer = Drawer(filterDrawerElement);

    filterDrawerBtn.addEventListener('click', () => filterDrawer.open());

    filterDrawerCloseBtns.forEach((btn) => {
      btn.addEventListener('click', () => filterDrawer.close());
    });
  };

  const init = () => {
    initSorting();
    initPriceFilter();
    initFilters();
    initFilterDrawer();

    ctx.on('collectionFilters:init', () => {
      initPriceFilter();
      initFilters();

      app.mount();
    });
  };

  /**
   * Init
   */
  init();
});
